/* istanbul ignore file */ /* this file is not used or will be deleted in other branches */
// @ts-nocheck
import React from 'react';
import useFieldApi from '@data-driven-forms/react-form-renderer/dist/cjs/use-field-api';
import componentTypes from '@data-driven-forms/react-form-renderer/dist/cjs/component-types';
import clsx from 'clsx';
import { FieldWrapper } from '@td-client-frontend/common';
import { useDdf } from '../../context/ddf-context';
import useFormApi from '@data-driven-forms/react-form-renderer/dist/cjs/use-form-api';
import { chronicCareCode } from '../../constants/formConstants';
import ReactTooltip from 'react-tooltip';
import { Info2 as ExclamationCircle } from '@td-client-frontend/icons';
import { getGroupNumber } from '../../utils/groups/helpers';

const getCheckBoxError = ({ isChronicCare, error, formError, name }) => {
  if (isChronicCare && name?.includes('group')) {
    const groupNumber = getGroupNumber(name);
    if (isFinite(groupNumber) && formError?.groups && formError?.groups[groupNumber]) {
      return Object.keys(formError?.groups[groupNumber]).some((code) => chronicCareCode.some((c) => code.includes(c)));
    }
    return error;
  }
  return error;
};

export function Checkbox({
  name,
  label,
  initialValue,
  className,
  skipLabel,
  skipError,
  toolTip,
  disabled,
  action,
  ...props
}) {
  const { getState } = useFormApi();
  const { input, handler, ...rest } = useFieldApi({
    ...props,
    name,
    type: 'checkbox',
    component: componentTypes.CHECKBOX,
    initialValue,
    toolTip,
    disabled,
    action
  });

  const isError: boolean = getCheckBoxError({
    isChronicCare: props?.isChronicCare,
    error: !!rest.meta.error,
    formError: getState().errors,
    name
  });

  const { handlers } = useDdf();

  const getProductStateKey = (productShortName) => {
    return `products--${productShortName}--isEnabled`;
  };

  const productDependencies = {
    DIABP: ['ADPP', 'CDPC'],
    WEIGM: ['ADVWM', 'COMWC'],
    DIABPCARE: ['CDPC'],
    WEIGMCARE: ['COMWC']
  };
  const bundleProductDependencies = {
    NUTPROMO: {
      or: [
        ['DIAB', 'DIABP', 'HYPT'],
        ['DIABCARE', 'DIABPCARE', 'HYPTCARE']
      ]
    }
  };

  const disable = (state, productShortName) => {
    state[getProductStateKey(productShortName)] = false;
  };

  const getGroupState = (name) => {
    const groupNumber = getGroupNumber(name);
    return getState().values.groups[groupNumber];
  };

  const conditionallyDisabled = (name, disabled) => {
    const state = getGroupState(name);
    if (disabled?.when) {
      return state[disabled.when] === disabled.is;
    }
    if (disabled?.conditions) {
      const useOr = disabled?.or === true;
      const conditionsMet = disabled?.conditions.reduce((condition, c) => {
        const result = state[c.when] === c.is;
        return useOr ? condition || result : condition && result;
      }, !useOr);
      return disabled?.not ? !conditionsMet : conditionsMet;
    }
    return disabled;
  };

  const isDisabled = conditionallyDisabled(name, disabled);

  const updateState = () => {
    const state = getGroupState(name);
    state['products--SPP--isEnabled'] = !isDisabled && state['products--SPP--isEnabled'];
    for (const [controllingProductName, dependentProductNames] of Object.entries(productDependencies)) {
      const controllingProductKey = getProductStateKey(controllingProductName);

      if (!state[controllingProductKey]) {
        dependentProductNames.forEach((depShortName) => {
          disable(state, depShortName);
        });
      }
    }
    for (const [bundleProductName, bundleDependencies] of Object.entries(bundleProductDependencies)) {
      const bundleProductKey = getProductStateKey(bundleProductName);
      if (state[bundleProductKey]) {
        const bundleMatch = bundleDependencies.or.some((dependencies) => {
          return dependencies.every((depShortName) => {
            return state[getProductStateKey(depShortName)];
          });
        });
        if (!bundleMatch) {
          disable(state, bundleProductName);
        }
      }
    }
  };

  const handleChange = (name) => {
    if (handlers) {
      updateState();
    }
  };

  return (
    <FieldWrapper name={name} skipLabel={skipLabel} skipError={skipError} className={className}>
      <div className="h-12 flex items-center space-x-3">
        <label className="flex justify-start items-start">
          <div
            className={clsx(
              'rounded w-5 h-5 flex flex-shrink-0 justify-center items-center mr-2',
              isError ? 'border-2 border-red-500' : 'field-with-icons',
              isDisabled ? 'bg-gray-15' : ''
            )}
          >
            <input
              type="checkbox"
              className="opacity-0 absolute"
              data-testid="checkbox-input"
              {...input}
              onInput={() => handleChange(name)}
              disabled={isDisabled}
            />
            <svg
              viewBox="0 0 20 20"
              className={clsx('fill-current w-3 h-3 text-primary-500 pointer-events-none', !input.checked && 'hidden')}
            >
              <path d="M0 11l2-2 5 5L18 3l2 2L7 18z" />
            </svg>
          </div>
          <div
            className={`${
              toolTip ? 'flex' : ''
            } font-effra text-black-default block tracking-wide text-sm font-normal mb-2`}
          >
            {label}
            {toolTip && (
              <>
                <ExclamationCircle
                  data-for="spp-tooltip"
                  data-tip={toolTip}
                  className="-mt-2 fill-current text-primary-500"
                />
                <ReactTooltip
                  id="spp-tooltip"
                  className="tooltip-custom"
                  place="right"
                  effect="solid"
                  type="light"
                  multiline
                  border
                  borderColor="#5b2f91"
                />
              </>
            )}
          </div>
        </label>
      </div>
    </FieldWrapper>
  );
}
